import React, { useState } from 'react';
import m from './GoalsStepMobile.module.css';
import s from '../DietPlanBuilder.module.css';
import { copy } from '../../copy';
import { fmtDate, fmtInt } from '../../lib/format';
import { Icon } from '../ui';
import { MACRO_KEYS, MACRO_META_NATIVE, type GoalsStepModel } from './goalsStep.shared';
import type { MacroKey } from '../../types';

const revealOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
  const el = e.currentTarget;
  if (el.type !== 'date') el.select?.();
  window.setTimeout(() => {
    if (!el.isConnected || document.activeElement !== el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  }, 280);
};

const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === 'Enter') e.currentTarget.blur();
};

function NumberField({
  value,
  onCommit,
  label,
  className,
}: {
  value: number;
  onCommit: (n: number) => void;
  label: string;
  className?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      className={className}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="off"
      enterKeyHint="done"
      aria-label={label}
      value={draft ?? String(value)}
      onFocus={e => { setDraft(String(value)); revealOnFocus(e); }}
      onChange={e => {
        const clean = e.target.value.replace(/\D/g, '').slice(0, 5);
        setDraft(clean);
        if (clean !== '') onCommit(Number(clean));
      }}
      onBlur={() => setDraft(null)}
      onKeyDown={blurOnEnter}
    />
  );
}

function MacroRow({
  k,
  g,
}: {
  k: MacroKey;
  g: GoalsStepModel;
}) {
  const meta = MACRO_META_NATIVE[k];
  return (
    <div className={m.macroRow} role="group" aria-label={meta.label}>
      <span className={m.dot} style={{ background: meta.bg }} aria-hidden />
      <div className={m.macroCopy}>
        <div className={m.macroName}>{meta.label}</div>
        <div className={m.macroGrams}>{g.grams[k]} g/day</div>
      </div>
      <button type="button" className={s.stepBtnXs} onClick={() => g.stepPct(k, -1)} aria-label={`Less ${meta.label}`}>
        <Icon.Minus size={16} />
      </button>
      <div className={m.pctWrap}>
        <NumberField className={m.pctInput} value={g.pct[k]} onCommit={n => g.setPct(k, n)} label={`${meta.label} percent`} />
        <span className={m.pctUnit}>%</span>
      </div>
      <button type="button" className={s.stepBtnXs} onClick={() => g.stepPct(k, 1)} aria-label={`More ${meta.label}`}>
        <Icon.Plus size={16} />
      </button>
    </div>
  );
}

export function GoalsStepMobile({ g }: { g: GoalsStepModel }) {
  return (
    <div className={m.page}>
      <label className={m.field}>
        <span className={m.label}>{copy.goals.name}</span>
        <input
          className={m.input}
          value={g.name}
          onChange={e => g.setName(e.target.value)}
          onFocus={revealOnFocus}
          onKeyDown={blurOnEnter}
          autoComplete="off"
          autoCapitalize="words"
          enterKeyHint="done"
        />
      </label>

      <div className={m.dates}>
        <label className={m.field}>
          <span className={m.label}>{copy.goals.starts}</span>
          <input
            className={m.date}
            type="date"
            value={g.from}
            min={g.minStartDate}
            onChange={e => g.setFrom(e.target.value)}
            onFocus={revealOnFocus}
            aria-invalid={g.datesPastStart}
          />
        </label>
        <label className={m.field}>
          <span className={m.label}>{copy.goals.ends}</span>
          <input
            className={m.date}
            type="date"
            value={g.to}
            min={g.from >= g.minStartDate ? g.from : g.minStartDate}
            onChange={e => g.setTo(e.target.value)}
            onFocus={revealOnFocus}
            aria-invalid={!g.datesOk}
          />
        </label>
      </div>
      {g.dateRangeLabel ? (
        <div className={m.rangeHint}>{g.dateRangeLabel}</div>
      ) : g.datesPastStart ? (
        <p className={m.error} role="alert">{copy.goals.pastStart}</p>
      ) : (
        <p className={m.error} role="alert">{copy.goals.badDates}</p>
      )}
      {g.overlap && (
        <p className={m.error} role="alert">
          {copy.goals.overlap(g.overlap.name, fmtDate(g.overlap.from), fmtDate(g.overlap.to))}{' '}
          <button type="button" className={m.overlapFixBtn} onClick={g.applySuggestedDates}>
            {copy.goals.useFreeDates(fmtDate(g.suggestedRange.from), fmtDate(g.suggestedRange.to))}
          </button>
        </p>
      )}

      <section className={m.card} aria-label={copy.goals.kcal}>
        <div className={m.cardHead}>
          <div className={m.cardTitle}>{copy.goals.kcal}</div>
          {g.templateHint && <div className={m.cardHint}>{g.templateHint}</div>}
        </div>
        <div className={s.kcalStepper}>
          <button type="button" className={s.stepBtn} onClick={() => g.stepKcal(-50)} aria-label="Less calories">
            <Icon.Minus size={20} />
          </button>
          <div className={s.stepVal}>
            <b>{fmtInt(g.kcal)}</b>
            <span className={s.muted} style={{ marginLeft: 4, fontWeight: 600 }}>kcal</span>
          </div>
          <button type="button" className={s.stepBtn} onClick={() => g.stepKcal(50)} aria-label="More calories">
            <Icon.Plus size={20} />
          </button>
        </div>
      </section>

      <section className={m.card} aria-labelledby="gm-split">
        <div id="gm-split" className={m.cardTitle}>{copy.goals.split}</div>
        <div className={s.macroPresetRow} role="group" aria-label="Macro preset">
          {g.presets.map(pr => (
            <button
              type="button"
              key={pr.label}
              className={s.chipPreset}
              data-on={pr.active}
              aria-pressed={pr.active}
              onClick={() => g.applyPreset(pr.pct, pr.label)}
            >
              {pr.label}
            </button>
          ))}
        </div>
        <div className={s.macroBar} aria-hidden>
          {MACRO_KEYS.map(k => (
            <span key={k} style={{ width: `${g.pct[k]}%`, background: MACRO_META_NATIVE[k].bg }} />
          ))}
        </div>
        <div className={m.macros}>
          {MACRO_KEYS.map(k => <MacroRow key={k} k={k} g={g} />)}
        </div>
        {!g.isBalanced && (
          <p className={m.error} role="status">Macro split is {g.sumPct}% — adjust to total 100%.</p>
        )}
      </section>

      <section className={m.card} aria-label={copy.goals.advanced}>
        <button type="button" className={m.advanced} onClick={g.openSplit}>
          <span className={m.advIcon} aria-hidden><Icon.Lines size={18} /></span>
          <span className={m.advCopy}>
            <span className={m.advTitle}>{copy.goals.advanced}</span>
            <span className={m.advSub}>{g.splitSummary}</span>
          </span>
          <span className={m.advChevron} aria-hidden><Icon.Chevron size={18} /></span>
        </button>
      </section>
    </div>
  );
}
